import { FunctionComponent, memo, useMemo, useState, useEffect, useRef, Suspense, lazy, useCallback } from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { useFormatMessage } from '../i18n-review/client/Msg';
import { AutocompleteHandle } from '../components/Autocomplete';
import { getIndexName } from '../constants';
import { useAuth } from '../hooks/useAuth';
import { useAppDispatch } from '../redux/hooks';
import { usePageContext } from 'vike-react/usePageContext';
import { FacetsProvider } from '../contexts/FacetsContext';
import { useSearchContext } from '../contexts/SearchContext';
import { SignInPromptModal } from '../components/auth/SignInPromptModal';
import { isToolsPage } from '../utils/routing/route-utils';
import { workspaceRouteOf } from '../utils/routing/workspace-routes';
import { JOBS_LISTING_SEGMENTS } from '../utils/constants/url-constants';
import { PerformanceProfiler, useRenderLogger } from '../utils/shared/performance-profiler';
// Direct imports (not the barrel): this file also dynamically imports MobileBottomNav/
// MobileMenuModal from components/navigation for code-splitting — statically importing anything
// through the barrel (components/navigation/index.ts) pulls those two into this same chunk too,
// defeating that split (confirmed via an INEFFECTIVE_DYNAMIC_IMPORT build warning).
import { NavigationNavbar } from '../components/navigation/NavigationNavbar';
import { MOBILE_BOTTOM_BAR_CONTAINER, MOBILE_NAV_STICKY_GROUP } from '../components/navigation/nav-live-index/nav-styles';
import { Logo } from '../components/navigation/Logo';
import { LanguageSelector } from '../components/navigation/LanguageSelector';
import Footer from '../components/Footer';
import { ErrorBoundary } from '../design-system/ErrorBoundary';
import { FacetsUpdater } from '../components/facets/FacetsUpdater';
import { DeferredFacetsLoader } from '../components/facets/DeferredFacetsLoader';
import type { DeferredFacetsRequest } from '../utils/facets/deferred-facets';
import { LiteShellProvider } from '../contexts/LiteShellContext';
import { DesktopFilterCategoriesMemo } from '../components/search/DesktopFilterCategories';
import { AppBanner } from '../components/app-download/AppBanner';
import { normalizeSupportedLocale } from '../utils/routing/locale-from-pathname';
import {
  cancelIdleScheduleHandle,
  scheduleIdleCallbackWithFallback,
} from '../utils/browser/scheduleIdleCallbackWithFallback';

// Lazy load the content that opens from the top bar (modals, mobile menu)
const MobileBottomNav = lazy(() =>
  import('../components/navigation/MobileBottomNav').then((m) => ({ default: m.MobileBottomNav }))
);
const MobileMenuModal = lazy(() => import('../components/navigation/MobileMenuModal'));

// Lazy load modal components to reduce initial bundle size
const AllFiltersModal = lazy(() => import('../components/AllFiltersModal'));

interface SearchWrapperProps extends React.PropsWithChildren {
  children: React.ReactNode;
  isSearchPage: boolean;
  /** When true, SRP uses minimal header and defers filter bar / mobile nav until interaction (Step 3) */
  useLiteShell?: boolean;
  serverState?: any;
  searchClientOverride?: any;
  disableAuth?: boolean;
  disableChrome?: boolean;
  ssrIsMobile?: boolean;
  ssrIsXl?: boolean;
  initialFacetsData?: any;
  /** Facets the SSR main query skipped, fetched after load (DeferredFacetsLoader). */
  deferredFacetsRequest?: DeferredFacetsRequest | null;
  /** When outside Vike tree (e.g. SRP island), pass URL/locale so chrome works without usePageContext(). */
  pathnameOverride?: string;
  urlOriginalOverride?: string;
  localeOverride?: string;
}

export const SearchWrapper: FunctionComponent<SearchWrapperProps> = (props) => {
  const renderCount = 0;

  // Log render summary every 5 renders (client-side only)
  if (false) {}

  const intl: IntlShape = useIntl();

  // Performance logging
  useRenderLogger('SearchWrapper', {
    isSearchPage: props.isSearchPage,
    disableChrome: props.disableChrome,
    disableAuth: props.disableAuth,
  });
  const memoIndexName = useMemo(() => getIndexName(intl.locale), [intl.locale]);

  // Get user from authenticated session (optionally disabled for SSR getServerState)
  const user = (props.disableAuth && typeof window === 'undefined')
    ? null
    : (useAuth().user);
  const [isAllFiltersModalOpen, setIsAllFiltersModalOpen] = useState(false);
  // Navbar live-index: the header's location segment on a non-search page navigates here and
  // asks for the filters modal (Location section) to open once on arrival, via a one-shot
  // localStorage flag set by nav-live-index/SearchLauncher.tsx. Cleared immediately so a later
  // plain visit does not reopen it.
  useEffect(() => {
    if (!props.isSearchPage || typeof window === 'undefined') return;
    try {
      if (window.localStorage.getItem('allFiltersModal.autoOpen') === '1') {
        window.localStorage.removeItem('allFiltersModal.autoOpen');
        setIsAllFiltersModalOpen(true);
      }
    } catch {
      // storage unavailable: nothing to auto-open
    }
  }, [props.isSearchPage]);
  // Which rail section AllFiltersModal currently has open, reported up so the nav's Location
  // button only highlights when the modal is genuinely showing the Location tab (not just
  // whenever the modal happens to be open on some other tab). See AllFiltersModal's
  // onSectionChange and DesktopFilterCategories' isLocationActive.
  const [allFiltersModalSection, setAllFiltersModalSection] = useState<string | null>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const dispatch = useAppDispatch();

  // Step 3: Lite shell – full filter bar / mobile nav load on interaction or after idle
  const useLiteShell = props.useLiteShell === true && props.isSearchPage;
  const [fullShellLoaded, setFullShellLoaded] = useState(false);
  const requestFullShell = useCallback(() => setFullShellLoaded(true), []);
  useEffect(() => {
    if (!useLiteShell) return;
    const handle = scheduleIdleCallbackWithFallback(() => setFullShellLoaded(true), { timeout: 4000 });
    return () => cancelIdleScheduleHandle(handle);
  }, [useLiteShell]);
  

  if (false && props.serverState) {}

  if (false) {}

  // A stable value, so a SearchWrapper render that changes none of these leaves its consumers alone.
  const liteShellValue = useMemo(
    () => ({
      isLiteShell: useLiteShell,
      fullShellLoaded: useLiteShell ? fullShellLoaded : undefined,
      requestFullShell: useLiteShell ? requestFullShell : undefined,
    }),
    [useLiteShell, fullShellLoaded, requestFullShell],
  );

  const pageContext = usePageContext();

  // Compute isSearch from pathname to complement prop (handles client navigations). Use overrides when outside Vike tree (e.g. SRP island).
  const pathname = (pageContext as any)?.urlPathname
    ?? props.pathnameOverride
    ?? (typeof window !== 'undefined' ? window.location.pathname : '/');

  const segs = String(pathname).split('/').filter(Boolean);
  const isLocale = segs[0] && ['en', 'fi', 'sv'].includes(segs[0]);
  // 'jobb' deliberately excluded: it's sv's job-DETAIL segment, not a listing segment
  // (sv's listing segment is 'lediga-jobb'). See url-constants.ts's JOBS_LISTING_SEGMENTS
  // for the 2026-09-02 regression this pattern caused when included here.
  const isJobsKeyword = (s?: string) => !!s && JOBS_LISTING_SEGMENTS.includes(s);
  // Support both with-locale (/en/jobs/...) and locale-stripped (/jobs/...)
  const computedIsSearch =
    segs.length === 0
      || (segs.length === 1 && (isLocale || isJobsKeyword(segs[0])))
      || (isLocale && isJobsKeyword(segs[1]))
      || (!isLocale && isJobsKeyword(segs[0]));
  const isSearch: boolean = !!props.isSearchPage || !!computedIsSearch;

  const content = (
    props.disableChrome ? (
      props.children ?? null
    ) : (
      <ErrorBoundary>
        <SearchWrapperContent
          {...props}
          isSearchPage={isSearch}
          intl={intl}
          isAllFiltersModalOpen={isAllFiltersModalOpen}
          setIsAllFiltersModalOpen={setIsAllFiltersModalOpen}
          allFiltersModalSection={allFiltersModalSection}
          setAllFiltersModalSection={setAllFiltersModalSection}
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
          user={user}
          useLiteShell={useLiteShell}
          fullShellLoaded={fullShellLoaded}
          requestFullShell={requestFullShell}
        />
      </ErrorBoundary>
    )
  );

  return (
    <PerformanceProfiler id="SearchWrapper">
      <LiteShellProvider value={liteShellValue}>
        <FacetsProvider initialFacetsData={props.initialFacetsData}>
          <FacetsUpdater />
          <DeferredFacetsLoader request={props.deferredFacetsRequest} />
          <div className="min-h-screen bg-jc-page w-full max-w-full">
            <ErrorBoundary>
              {content}
            </ErrorBoundary>
          </div>
        </FacetsProvider>
      </LiteShellProvider>
    </PerformanceProfiler>
  );
};

interface SearchWrapperContentProps extends SearchWrapperProps {
  intl: IntlShape;
  isAllFiltersModalOpen: boolean;
  setIsAllFiltersModalOpen: (value: boolean) => void;
  allFiltersModalSection: string | null;
  setAllFiltersModalSection: (value: string | null) => void;
  isMobileMenuOpen?: boolean;
  setIsMobileMenuOpen?: (open: boolean) => void;
  user: any | null | undefined;
  useLiteShell?: boolean;
  fullShellLoaded?: boolean;
  requestFullShell?: () => void;
}

// Memoized: the navbar and search chrome (~200 components) re-render only when their own props,
// state or context change, not on every SearchWrapper render (such as AuthProvider settling its
// loading flag right after hydration, which re-rendered all of it in a blocking task on every page).
const SearchWrapperContent = memo(function SearchWrapperContent(props: SearchWrapperContentProps) {
  const formatMessage = useFormatMessage();
  const pageContext = usePageContext();
  // Map view renders its own fixed-to-viewport layout (see MapView's height calc) and no longer
  // renders the SRP footer below it (see SrpView.tsx) — so the page content should exactly match
  // the viewport with no scroll, instead of the `overflow-y-auto` list/grid views need. Without
  // this, a trackpad/touch drag meant for panning the map could scroll the whole page instead.
  const { viewMode } = useSearchContext();
  const isMapView = props.isSearchPage && viewMode === 'map';
  const urlOriginal = (pageContext as any)?.urlOriginal ?? props.urlOriginalOverride ?? (typeof window !== 'undefined' ? window.location.href : '/');
  const fullUrlContent = urlOriginal;
  const pathnameForClient = fullUrlContent.startsWith("http") ? new URL(fullUrlContent).pathname : fullUrlContent;
  // SSR/CSR compatible pathname
  const url = urlOriginal;
  const pathname = url.startsWith("http") ? new URL(url).pathname : url;
  const isLocationHubPage =
    pathname.includes('/locations') ||
    pathname.includes('/sijainnit') ||
    pathname.includes('/platser');
  const isProfilePage = pathname.includes('/profile');
  // The employers hub and its sub-pages lay out their own full-width container, so the content
  // area adds no side padding there (which would also inset the site footer). `pathname` is the raw
  // urlOriginal during SSR (query string included), so the segment may be followed by ? or #.
  const isEmployersHubPage = /^\/(?:(?:en|fi|sv)\/)?(?:employers|tyonantajat|alla-arbetsgivare|best-companies|parhaat-yritykset|basta-foretag)(?:[/?#]|$)/.test(pathname);
  const isDashboardOrLocationHubPage =
    pathname.includes('/backoffice/crawler') || pathname.includes('/backoffice/dashboard') || isLocationHubPage || isProfilePage;
  const onToolsPage = isToolsPage(pathname);
  // Scout, My Jobs, Searches & Alerts, the Watchlist and the account pages, at their public address
  // ("/fi/scout") or the logical one ("/en/agent"): a regex on "/agent" alone stopped matching when
  // Scout moved to /scout (2026-11-02) and brought the footer and site search back onto the chat.
  const workspaceRoute = workspaceRouteOf(pathname);
  // The Agent page's question box is its search: no site search bar (top or bottom) competes with it.
  const isAgentPage = workspaceRoute === '/agent';
  // Tool pages are app screens, not content: no site footer under them (owner 2026-11-02).
  const isToolPage = onToolsPage || workspaceRoute !== null;
  // Phase 2 item 2.7 (navbar live-index redesign): the bottom search/filters/menu bar used to
  // mount only on the SRP, leaving every other mobile page with no way to search short of
  // opening the hamburger menu; the redesign's mobile top bar assumed this bar existed
  // everywhere. /tools (the My Jobs hub) has its own tab bar for its kanban board, so it stays
  // excluded the same way the job detail page already excludes itself (MobileBottomNav's own
  // isJobDetailPage early return, unchanged). Since 2026-11-01 the bar is the search row under
  // the header, so pages no longer reserve bottom padding for it.
  const showMobileBottomNav = props.isSearchPage || (!onToolsPage && !isAgentPage);
  // Whether that bar is actually on screen (lite shell defers it; MobileBottomNav itself returns
  // null on job detail pages, same regex as its own check). When it is, it owns the mobile
  // hamburger and the top one hides.
  const mobileBottomNavRendered =
    showMobileBottomNav && !(props.useLiteShell && !props.fullShellLoaded) && !/\/job\/[^/]+\/[^/]+/.test(pathname);

  // Parse current URL to get location if present
  // We no longer need the custom hook; URL creation uses the router in InstantSearch

  // Ref for the desktop autocomplete component. MobileBottomNav no longer mounts an
  // Autocomplete instance of its own (it renders a plain SearchPanel trigger instead), so
  // there is nothing on the mobile side left to close here.
  const desktopAutocompleteRef = useRef<AutocompleteHandle>(null);

  // Helper to close the (desktop) autocomplete instance
  const closeAllAutocompletes = useCallback(() => {
    desktopAutocompleteRef.current?.close();
  }, []);

  // Callback to close filter modals when autocomplete opens
  const handleAutocompleteOpen = useCallback(() => {
    if (props.isAllFiltersModalOpen) {
      props.setIsAllFiltersModalOpen?.(false);
    }
  }, [props.isAllFiltersModalOpen, props.setIsAllFiltersModalOpen]);

  const clearSearch = async (event?: React.MouseEvent<HTMLAnchorElement>): Promise<void> => {
    // Prevent default so we control the destination (home, not a cleared search URL).
    if (event) {
      event.preventDefault();
    }
    const locale = ((pageContext as any)?.locale ?? (props as any).localeOverride ?? 'en') as 'en' | 'fi' | 'sv';
    // Hard-navigate to the locale home (landing page). The landing page uses
    // injectScriptsAt:null, so a full server fetch is required — no Vike navigate().
    // React state is discarded on the reload, so no explicit reset is needed.
    window.location.assign(locale === 'en' ? '/' : `/${locale}`);
  };

  // Render logging for debugging
  useRenderLogger('SearchWrapperContent', {
    isSearchPage: props.isSearchPage,
    isAllFiltersModalOpen: props.isAllFiltersModalOpen,
    isMobileMenuOpen: props.isMobileMenuOpen,
  });

  // Add '/' shortcut for search button on non-search pages
  useEffect(() => {
    if (props.isSearchPage) return;
    const handler = (e: KeyboardEvent) => {
      if (
        e.key === '/' &&
        !(document.activeElement instanceof HTMLInputElement) &&
        !(document.activeElement instanceof HTMLTextAreaElement) &&
        !(document.activeElement instanceof HTMLSelectElement)
      ) {
        e.preventDefault();
        const btn = document.getElementById('search-shortcut-btn');
        if (btn) {
          (btn as HTMLElement).focus();
          (btn as HTMLElement).click();
        }
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [props.isSearchPage]);

  // Holds the phone search row's exact height (same container, a 44px control) until the row loads,
  // so the page never jumps down when it arrives at the top.
  const mobileSearchRowPlaceholder = (
    <div className={MOBILE_BOTTOM_BAR_CONTAINER} aria-hidden="true">
      <div className="h-11 flex-1" />
    </div>
  );

  // Pages scroll the document (2026-11-01), so browsers give them native scrolling: Safari's
  // toolbar collapses, a status-bar tap goes to the top, route changes start at the top, and the
  // navbar's scroll collapse (useScrollCollapse, which reads window.scrollY) works. Before, every
  // page scrolled inside the main column below, which read as "no smooth scrolling" (website
  // feedback, 2026-10-31). Only the map and Scout's chat keep a fixed, screen-high shell: the map
  // fills the screen and the chat docks its question box at the bottom.
  const fixedShell = isMapView || isAgentPage;

  return (
    <div
      className={
        (fixedShell
          ? 'relative flex flex-col h-screen mobile-viewport-height overflow-hidden lg:overflow-visible'
          : 'relative flex flex-col min-h-screen') + ' bg-jc-page'
      }
    >
      <SignInPromptModal />
      {/* Lazy load AllFiltersModal only when needed */}
      {props.isAllFiltersModalOpen && (
        <Suspense fallback={null}>
          <AllFiltersModal
            isOpen={true}
            onRequestClose={() => props.setIsAllFiltersModalOpen(false)}
            onCloseComplete={() => {
              if (props.isAllFiltersModalOpen) props.setIsAllFiltersModalOpen(false);
              props.setAllFiltersModalSection(null);
            }}
            onSectionChange={props.setAllFiltersModalSection}
          />
        </Suspense>
      )}
      {/* Top Header - Navigation and Search Controls. TEC navbar redesign item 9: this used to
          be a second `<header role="banner">` wrapping NavigationNavbar/DesktopFilterCategories,
          each of which already renders its own `role="banner"` header, nesting one banner
          landmark inside another. Neither child needs an outer landmark of its own, so this is
          now a plain, non-landmark element and the page has exactly one banner. */}
      <AppBanner locale={normalizeSupportedLocale(props.intl.locale)} src={props.isSearchPage ? 'srp_banner' : 'web_banner'} />
      {/* The header and the phone search row stick together (MOBILE_NAV_STICKY_GROUP); from lg the
          group is `contents`, so the navbars' own `sticky top-0` holds against the scrolling
          document. */}
      <div className={MOBILE_NAV_STICKY_GROUP}>
        {!props.isSearchPage && (
          <NavigationNavbar
            intl={props.intl}
            isMobileMenuOpen={props.isMobileMenuOpen}
            setIsMobileMenuOpen={props.setIsMobileMenuOpen}
            clearSearch={clearSearch}
            menuButtonDesktopOnly={mobileBottomNavRendered}
            hideSearch={isAgentPage}
            user={props.user}
          />
        )}
        {/* SRP desktop top bar: logo, autocomplete, filter buttons, hamburger – static, no skeleton; only the content that opens (modals, menu) is lazy-loaded */}
        {props.isSearchPage && (
          <DesktopFilterCategoriesMemo
            intl={props.intl}
            isMobileMenuOpen={props.isMobileMenuOpen}
            setIsMobileMenuOpen={props.setIsMobileMenuOpen}
            isAllFiltersModalOpen={props.isAllFiltersModalOpen}
            setIsAllFiltersModalOpen={props.setIsAllFiltersModalOpen}
            allFiltersModalSection={props.allFiltersModalSection}
            clearSearch={clearSearch}
            user={props.user}
            closeAutocomplete={closeAllAutocompletes}
            autocompleteRef={desktopAutocompleteRef}
            onAutocompleteOpen={handleAutocompleteOpen}
          />
        )}
        {/* Phone search row (search, filters, menu), under the header since 2026-11-01 (it was a
            bar fixed to the bottom of the screen; people look for search at the top). Lazy
            loaded; skipped in the lite shell until fullShellLoaded. Not a landmark wrapper:
            MobileBottomNav.tsx's own root is a labelled `<nav>`. */}
        {showMobileBottomNav && (props.useLiteShell && !props.fullShellLoaded ? (
          mobileSearchRowPlaceholder
        ) : (
          <div className="lg:hidden">
            <Suspense fallback={mobileSearchRowPlaceholder}>
              <MobileBottomNav
                formatMessage={formatMessage}
                isSearchPage={props.isSearchPage}
                isAllFiltersModalOpen={props.isAllFiltersModalOpen}
                setIsAllFiltersModalOpen={props.setIsAllFiltersModalOpen}
                isMobileMenuOpen={props.isMobileMenuOpen}
                setIsMobileMenuOpen={props.setIsMobileMenuOpen}
                closeAllAutocompletes={closeAllAutocompletes}
                handleAutocompleteOpen={handleAutocompleteOpen}
              />
            </Suspense>
          </div>
        ))}
      </div>
      {/* Content wrapper: contains the menu and the content */}
      <div className={`flex-1 flex lg:flex-row lg:relative min-h-0 ${fixedShell ? 'lg:overflow-hidden' : ''}`}>
        {/* Mobile Menu Modal — always mounted (same as non-SRP); portaled to body inside the component */}
        {props.isMobileMenuOpen !== undefined && (
          <Suspense fallback={null}>
            <MobileMenuModal
              isOpen={props.isMobileMenuOpen}
              onRequestClose={() => props.setIsMobileMenuOpen?.(false)}
              onCloseComplete={() => {
                if (props.isMobileMenuOpen) props.setIsMobileMenuOpen?.(false);
              }}
              fullUrlContent={fullUrlContent}
              intl={props.intl}
            />
          </Suspense>
        )}
        {/* Main content - flex-1 allows it to compress when keyboard appears; min-w-0 so SRP container can shrink for container queries */}
        {/* The menu is an overlay with a scrim on every width (same as static pages); the page never reflows under it */}
        <div
          data-srp-scroll-region={isLocationHubPage ? undefined : ''}
          className={`flex-1 min-w-0 bg-jc-page ${
            isLocationHubPage
              ? 'overflow-x-hidden lg:overflow-x-visible overflow-y-visible min-h-screen'
              : isMapView
              ? 'overflow-hidden min-h-0'
              : isAgentPage
              ? 'overflow-x-hidden overflow-y-auto min-h-0 overscroll-y-contain'
              : // clip, not hidden: a hidden overflow would make this a scroll container again and
                // break the sticky rail and headers inside it.
                'overflow-x-clip'
          } ${
            onToolsPage
              ? ''
              : isDashboardOrLocationHubPage || isEmployersHubPage
              ? 'pt-1 sm:pt-1 md:pt-2'
              : props.isSearchPage
              ? // Map view drops the top padding at every width: the map sits flush under the
                // header (its view switch floats over the map, there's no toolbar row above it
                // to give breathing room to), and any padding here is just a dead strip.
                isMapView
                ? 'pt-0'
                : 'pt-1 sm:pt-1 md:pt-2'
              : isAgentPage
              ? // No bottom bar here, so no room kept for it: the question box docks at the very bottom.
                'pt-1 sm:pt-1 md:pt-2 pb-0 sm:px-0 md:px-2 lg:px-8'
              : 'pt-1 sm:pt-1 md:pt-2 sm:px-0 md:px-2 lg:px-8'
          }`}
        >
          {props.children}
          {/* Site footer is chrome-owned for non-SRP pages; the SRP renders its own inside SrpView. It bleeds
              past the container's side gutter where there is one, so it spans the full width (2026-10-15).
              Tool pages (Scout, My Jobs, Searches, Watchlist, account) have none: they are app screens, and on
              Scout, a full-height chat, a footer under the docked box only adds scroll. */}
          {!props.isSearchPage && !isToolPage && (
            <Footer bleedMargins={!(onToolsPage || isDashboardOrLocationHubPage || isEmployersHubPage)} />
          )}
        </div>
      </div>
    </div>
  );
});
